import React, { useEffect, useState } from 'react';
import { CategoryManagementContext } from '.';
import {
	allOption,
	checkAndAssignDefaultValues,
	getDateFormat,
} from 'CategoryManagement/utils';
import {
	deleteCategory,
	getCMSidebarConfig,
	getKpisForCategoryManagement,
} from 'utils/apiRequests';
import { isUndefined } from 'lodash';
import { formatNumber, formatSpendWithDecimalAndSymbol } from 'utils/number';
import { getRequiredCustomFilterObjectFormat } from '../../CategoryManagement/utils';

///Taxonomy to be added to the file in 1,2,3 where it will be passed to the sidebar
function buildHierarchy(data, parentId = '/', level = 0) {
	const nodes = [];

	data.forEach(item => {
		if (item.parentID === parentId && item.level >= level) {
			const children = buildHierarchy(data, item.id, item.level);
			if (children.length > 0) {
				item.children = children;
			} else {
				item.children = [];
			}
			nodes.push(item);
		}
	});

	return nodes;
}

const initialCategoryName = allOption.id;

const CategoryManagementContextProvider = ({
	children,
	dateRangeData,
	currencySymbol,
	hideDecimals,
}) => {
	const { wholeRange, selectedRange } = dateRangeData;
	const minMaxDates = getDateFormat(selectedRange);
	const [globalData, setGlobalData] = useState({
		isFetching: true,
		currentSelectCategory: {
			categoryName: initialCategoryName,
		},
		sidebarConfig: {
			data: [],
			flatData: [],
			isDeleting: false,
		},
		dateRangeData: {
			srcRange: wholeRange,
			stateRange: selectedRange,
			...minMaxDates,
		},
		kpi: {
			isLoading: false,
			data: {
				totalSpend: 0,
				totalSuppliers: 0,
				businessUnit: 0,
				costCenters: 0,
				avgVendorSpend: 0,
				country: 0,
				vendorCount: 0,
			},
		},
		segmentationUpdatedCount: 0,
		isSourceable: false, // sourceable or total spend
		isDirect: true, // direct or indirect
		taxonomyType: 2,
		dateRangeDataSpendByYear: {
			srcRange: wholeRange,
			stateRange: selectedRange,
			...minMaxDates,
		},
		customFilters: [],
		customFiltersValues: [],
	});

	useEffect(
		() => {
			// if (globalData.isFetching) {
			fetchSidebarData();
			// }
		}, // eslint-disable-next-line
		[globalData.taxonomyType]
	);

	useEffect(
		() => {
			if (
				!isUndefined(globalData.currentSelectCategory.categoryName) &&
				!isUndefined(globalData.dateRangeData.stateRange) &&
				globalData.currentSelectCategory.categoryName !== ''
			) {
				if (!globalData.kpi.isLoading) {
					setGlobalData(prevState => ({
						...prevState,
						kpi: {
							...prevState.kpi,
							isLoading: true,
						},
					}));
					const minMaxDates = getDateFormat(
						globalData.dateRangeData.stateRange
					);
					const payload = {
						categoryName: globalData.currentSelectCategory.categoryName,
						isSourceable: globalData.isSourceable,
						aptaxonomy: globalData.isDirect,
						...minMaxDates,
						taxonomyType: globalData.taxonomyType,
					};
					if (
						Array.isArray(globalData.customFiltersValues) &&
						globalData.customFiltersValues.length > 0
					) {
						payload.customfilter = getRequiredCustomFilterObjectFormat(
							globalData.customFilters,
							globalData.customFiltersValues
						);
					}
					getKpisForCategoryManagement(payload)
						.then(response => {
							if (response.succeeded) {
								if (!!Object.keys(response.data).length) {
									const kpiData = {
										totalSpend: formatSpendWithDecimalAndSymbol(
											response.data.total_spend,
											currencySymbol,
											hideDecimals,
											2
										).toUpperCase(),
										totalSuppliers: formatNumber(response.data.total_suppliers),
										businessUnit: formatNumber(response.data.business_unit),
										costCenters: formatNumber(response.data.cost_centers),
										avgVendorSpend: formatSpendWithDecimalAndSymbol(
											response.data.avg_vendor_spend,
											currencySymbol,
											hideDecimals,
											2
										).toUpperCase(),
										country: formatNumber(response.data.country),
										vendorCount: formatNumber(response.data.vendor_count_kpi),
									};
									const result = checkAndAssignDefaultValues(kpiData, 0);
									setGlobalData(prevState => ({
										...prevState,
										kpi: {
											...prevState.kpi,
											data: result,
											isLoading: false,
										},
									}));
								} else {
									setGlobalData(prevState => ({
										...prevState,
										kpi: {
											...prevState.kpi,
											isLoading: true,
										},
									}));
								}
							} else {
								setGlobalData(prevState => ({
									...prevState,
									kpi: {
										...prevState.kpi,
										isLoading: true,
									},
								}));
							}
						})
						.catch(() =>
							setGlobalData(prevState => ({
								...prevState,
								kpi: {
									...prevState.kpi,
									isLoading: false,
								},
							}))
						);
				}
			}
		}, // eslint-disable-next-line
		[
			globalData.currentSelectCategory.categoryName,
			globalData.dateRangeData.stateRange,
			globalData.isDirect,
			globalData.isSourceable,
			globalData.taxonomyType,
			globalData.customFilters,
			globalData.customFiltersValues,
		]
	);

	const handleCategoryChange = value => {
		setGlobalData(prevState => ({
			...prevState,
			currentSelectCategory: {
				categoryName:
					globalData.taxonomyType === 2
						? value
						: value === '/All/'
							? value
							: value.substring(value.indexOf('/', 1)),
			},
		}));
	};

	const handleCategoryDelete = value => {
		setGlobalData(prevState => ({
			...prevState,
			sidebarConfig: {
				...prevState.sidebarConfig,
				isDeleting: true,
			},
		}));

		deleteCategory(value).then(response => {
			if (response.succeeded) {
				setGlobalData(prevState => ({
					...prevState,
					sidebarConfig: {
						...prevState.sidebarConfig,
						isDeleting: false,
					},
				}));
				if (value === globalData.currentSelectCategory.categoryName) {
					handleCategoryChange(allOption.id);
				}
				fetchSidebarData();
			} else {
				setGlobalData(prevState => ({
					...prevState,
					sidebarConfig: {
						...prevState.sidebarConfig,
						isDeleting: false,
					},
				}));
			}
		});
	};

	const handleDateRangeChange = value => {
		const minMaxDates = getDateFormat(value);
		setGlobalData(prevState => ({
			...prevState,
			dateRangeData: {
				...prevState.dateRangeData,
				stateRange: value,
				...minMaxDates,
			},
		}));
	};

	const handleSegmentationUpdate = () => {
		setGlobalData(prevState => ({
			...prevState,
			segmentationUpdatedCount: prevState.segmentationUpdatedCount + 1,
		}));
	};

	const fetchSidebarData = () => {
		try {
			setGlobalData(prevState => ({
				...prevState,
				isFetching: true,
			}));
			const payload = {
				taxonomyType: globalData.taxonomyType,
			};
			getCMSidebarConfig(payload)
				.then(sidebarConfigData => {
					if (sidebarConfigData.length > 0) {
						let hierarchyData = buildHierarchy(sidebarConfigData);
						hierarchyData.unshift(allOption);
						setGlobalData(prevState => ({
							...prevState,
							isFetching: false,
							sidebarConfig: {
								...prevState.sidebarConfig,
								data: hierarchyData,
								flatData: sidebarConfigData,
							},
						}));
					} else {
						setGlobalData(prevState => ({
							...prevState,
							isFetching: false,
							sidebarConfig: {
								...prevState.sidebarConfig,
								data: [],
								flatData: [],
							},
						}));
					}
				})
				.catch(() => {
					setGlobalData(prevState => ({
						...prevState,
						isFetching: false,
					}));
				});
		} catch (error) {
			setGlobalData(prevState => ({
				...prevState,
				isFetching: false,
			}));
		}
	};

	const handleSourceableTotalSpendChange = value =>
		setGlobalData(prevState => ({
			...prevState,
			isSourceable: JSON.parse(value),
		}));

	const handleDirectIndirectChange = value =>
		setGlobalData(prevState => ({
			...prevState,
			isDirect: JSON.parse(value),
			taxonomyType: 3,
		}));

	const handleTaxonomyTypeChange = value =>
		setGlobalData(prevState => ({
			...prevState,
			taxonomyType: Number(value),
			currentSelectCategory: {
				categoryName: initialCategoryName,
			},
		}));

	const handleDateRangeChangeForSpendByYear = value => {
		const minMaxDates = getDateFormat(value);
		setGlobalData(prevState => ({
			...prevState,
			dateRangeDataSpendByYear: {
				...prevState.dateRangeDataSpendByYear,
				stateRange: value,
				...minMaxDates,
			},
		}));
	};

	const handleCustomFilterChange = value => {
		setGlobalData(prevState => ({
			...prevState,
			...value,
		}));
	};

	return (
		<CategoryManagementContext.Provider
			value={{
				...globalData,
				fetchSidebarData,
				handleDateRangeChange,
				handleCategoryChange,
				handleCategoryDelete,
				handleSegmentationUpdate,
				handleSourceableTotalSpendChange,
				handleDirectIndirectChange,
				handleDateRangeChangeForSpendByYear,
				handleTaxonomyTypeChange,
				handleCustomFilterChange,
			}}
		>
			{children}
		</CategoryManagementContext.Provider>
	);
};

export { CategoryManagementContextProvider, CategoryManagementContext };
